import { useState } from 'react';
import { Check, Upload } from 'lucide-react';
import { ErrorCode, type ChatCard } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { ApiError, errorMessage } from '../../../lib/api';
import { chatAppointmentIcsHref } from '../api';
import type { PortalTicketCard } from '../types';
import styles from './Questions.module.css';

type SlotsCardData = Extract<ChatCard, { kind: 'slots' }>;
type AppointmentCardData = Extract<ChatCard, { kind: 'appointment' }>;
type Slot = SlotsCardData['slots'][number];

/**
 * Portal 23: three free times of the advisor (first preselected, `--accent-soft` selection) and „Termin verbindlich
 * anfragen". A 409 `SLOT_TAKEN` replaces the times with the fresh ones of `details.slots` and shows the server text.
 */
export function SlotsCard({
  card,
  active,
  onBook,
}: {
  card: SlotsCardData;
  active: boolean;
  onBook: (eventTypeId: string, startsAt: string) => Promise<void>;
}) {
  const [slots, setSlots] = useState<Slot[]>(card.slots);
  const [eventTypeId, setEventTypeId] = useState(card.eventTypeId);
  const [selected, setSelected] = useState<string | null>(card.slots[0]?.startsAt ?? null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function book() {
    if (!selected) return;
    setPending(true);
    setError(null);
    try {
      await onBook(eventTypeId, selected);
    } catch (cause) {
      setError(errorMessage(cause));
      if (cause instanceof ApiError && cause.code === ErrorCode.SLOT_TAKEN) {
        const fresh = cause.details?.['slots'];
        const type = cause.details?.['eventTypeId'];
        if (Array.isArray(fresh)) {
          const next = fresh as Slot[];
          setSlots(next);
          setSelected(next[0]?.startsAt ?? null);
        }
        if (typeof type === 'string') setEventTypeId(type);
      }
    } finally {
      setPending(false);
    }
  }

  if (slots.length === 0) return null;
  return (
    <div className={styles.slotsCard}>
      <div className={styles.slots} role="radiogroup" aria-label="Freie Termine">
        {slots.map((slot) => {
          const isSelected = slot.startsAt === selected;
          return (
            <button
              key={slot.startsAt}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={clsx(styles.slot, isSelected && active && styles.slotSelected)}
              onClick={() => setSelected(slot.startsAt)}
              disabled={!active || pending}
            >
              <span className={styles.slotLabel}>{slot.label}</span>
              <span className={styles.slotChannel}>{slot.channelLabel}</span>
            </button>
          );
        })}
      </div>
      {active ? (
        <button
          type="button"
          className={clsx('pressable', styles.bookButton)}
          onClick={() => void book()}
          disabled={!selected || pending}
        >
          Termin verbindlich anfragen
        </button>
      ) : null}
      {error ? (
        <div className={styles.cardError} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Portal 24: „Termin angefragt" (success card) with „Zum Kalender hinzufügen" (ICS) and „Absagen" (text link, asks
 * first). A confirmed appointment reads „Termin gebucht" and offers „Verschieben" (the booking page); a cancelled one
 * is shown neutral without actions.
 */
export function AppointmentCard({
  card,
  onCancel,
  preview,
}: {
  card: AppointmentCardData;
  onCancel: (appointmentId: string) => Promise<boolean>;
  preview?: boolean | undefined;
}) {
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const cancelled = card.status === 'cancelled';
  return (
    <div className={clsx(styles.appointmentCard, cancelled && styles.appointmentCancelled)}>
      <div className={styles.appointmentHead}>
        {cancelled ? null : <Check size={17} strokeWidth={2} aria-hidden="true" />}
        <span className={styles.appointmentEyebrow}>{card.eyebrow}</span>
      </div>
      <div className={styles.appointmentWhen}>{card.whenLabel}</div>
      <div className={styles.appointmentWith}>{card.withLine}</div>
      {cancelled || preview ? null : confirming ? (
        <div className={styles.appointmentActions}>
          <span className={styles.confirmText}>Termin wirklich absagen?</span>
          <button
            type="button"
            className={styles.cardButton}
            disabled={pending}
            onClick={() => {
              setPending(true);
              void onCancel(card.appointmentId).finally(() => {
                setPending(false);
                setConfirming(false);
              });
            }}
          >
            Ja, absagen
          </button>
          <button type="button" className={styles.cardLink} onClick={() => setConfirming(false)}>
            Nein
          </button>
        </div>
      ) : (
        <div className={styles.appointmentActions}>
          <a
            className={styles.cardButton}
            href={chatAppointmentIcsHref(card.appointmentId)}
            download="termin.ics"
          >
            Zum Kalender hinzufügen
          </a>
          {card.status === 'confirmed' && card.manageUrl ? (
            <a className={styles.cardLink} href={card.manageUrl}>
              Verschieben
            </a>
          ) : null}
          {card.canCancel ? (
            <button type="button" className={styles.cardLink} onClick={() => setConfirming(true)}>
              Absagen
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Portal 24 „Das wurde übermittelt": the note the advisor received, verbatim. */
export function TransmittedCard({ title, text }: { title: string; text: string }) {
  return (
    <div className={styles.transmitted}>
      <div className={styles.eyebrow}>{title}</div>
      <p className={styles.transmittedText}>{text}</p>
    </div>
  );
}

const TONE_CLASS = {
  info: styles.pillInfo,
  warning: styles.pillWarning,
  success: styles.pillSuccess,
  neutral: styles.pillNeutral,
} as const;

/** Portal 26 ticket card: number, status pill, title, meta and the three customer steps. */
export function TicketCardView({
  ticket,
  compact,
}: {
  ticket: PortalTicketCard;
  compact?: boolean | undefined;
}) {
  return (
    <div className={clsx(styles.ticketCard, compact && styles.ticketCompact)}>
      <div className={styles.ticketHead}>
        <span className={styles.eyebrow}>Ticket {ticket.numberLabel}</span>
        <span className={clsx(styles.pill, TONE_CLASS[ticket.tone])}>{ticket.statusLabel}</span>
      </div>
      <div className={styles.ticketTitle}>{ticket.title}</div>
      <div className={styles.ticketMeta}>{ticket.meta}</div>
      {compact ? null : (
        <ol className={styles.ticketSteps}>
          {ticket.steps.map((step) => (
            <li key={step.label} className={styles.ticketStep}>
              <span
                className={clsx(styles.ticketDot, step.done && styles.ticketDotDone)}
                aria-hidden="true"
              />
              <div>
                <div className={clsx(styles.ticketStepLabel, step.done && styles.ticketStepDone)}>
                  {step.label}
                  <span className={styles.srOnly}>{step.done ? ', erledigt' : ', offen'}</span>
                </div>
                {step.detail ? <div className={styles.ticketStepDetail}>{step.detail}</div> : null}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** Portal 25 „Datei hinzufügen · optional": the receipt for the support request (PDF/JPG/PNG ≤ 10 MB). */
export function AttachmentRequestCard({
  label,
  hint,
  active,
  onPick,
}: {
  label: string;
  hint: string;
  active: boolean;
  onPick: (file: File) => void;
}) {
  if (!active) {
    return (
      <div className={clsx(styles.attachmentRow, styles.attachmentInactive)}>
        <Upload size={18} strokeWidth={1.5} aria-hidden="true" />
        <span className={styles.attachmentLabel}>{label}</span>
        <span className={styles.attachmentHint}>{hint}</span>
      </div>
    );
  }
  return (
    <label className={styles.attachmentRow}>
      <Upload size={18} strokeWidth={1.5} aria-hidden="true" />
      <span className={styles.attachmentLabel}>{label}</span>
      <span className={styles.attachmentHint}>{hint}</span>
      <input
        type="file"
        className={styles.srOnly}
        accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onPick(file);
        }}
      />
    </label>
  );
}
